import { ChangeEvent } from 'react';
import { Controller } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import {
  ButtonSecondary,
  ButtonSize,
  formatPhone,
  IconClose,
  IconExpand,
  IconLoader,
  InputSize,
  InputText,
  PermissionEnum,
} from '@ui/src';
import InfoRow from '../../common/info-row';
import StepPartBlock from '../../common/step-part-block';
import { NewApplicationStatus } from '../../../../hoc/common/types';
import { ContactsTypes } from './types';

import commonStyles from '../../common/styles.module.css';
import cn from '../styles.module.css';

const Contacts = ({
  control,
  clearErrors,
  errors,
  hasSumSubId,
  isEditable,
  status,
  regulatedCompanyScheme,
  questionnarieControl,
  questionnarieClearErrors,
  questionnarieErrors,
  activePermissions,
  additionalEmails,
  onChangeEmail,
  onDeleteAdditionalEmails,
  setAdditionalEmails,
  questionnarieIsLoading,
}: ContactsTypes) => (
  <>
    <StepPartBlock title="Contacts" zIndex={29}>
      <InfoRow title="Phone number" id="phone" lessPadding>
        <Controller
          control={control}
          name="phone"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="phone"
              placeholder="Enter phone number..."
              value={value ? formatPhone(value) : ''}
              onChange={(e) => {
                onChange(e);
                clearErrors(['phone']);
              }}
              size={InputSize.SMALL}
              error={errors?.phone?.message}
              className="borderless"
              labelClassName={commonStyles.newApplicationInputLabel}
              tabIndex={-1}
              disabled
            />
          )}
        />
      </InfoRow>
      <InfoRow title="E-mail (optional)" id="email" lessPadding>
        <Controller
          control={control}
          name="email"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="email"
              placeholder="Enter email..."
              value={value}
              onChange={(e) => {
                onChange(e);
                clearErrors(['email']);
              }}
              size={InputSize.SMALL}
              className="borderless"
              error={errors?.email?.message}
              tabIndex={
                hasSumSubId ||
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              disabled={
                hasSumSubId ||
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
            />
          )}
        />
      </InfoRow>
    </StepPartBlock>
    {regulatedCompanyScheme && (
      <StepPartBlock title="Additional Contacts">
        <>
          {questionnarieIsLoading && (
            <div className={commonStyles.dictionaryLoader}>
              <IconLoader />
            </div>
          )}
          {!questionnarieIsLoading && (
            <>
              <InfoRow
                title="Home phone number"
                id="homePhoneNumber"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="homePhoneNumber"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <InputText
                      name="homePhoneNumber"
                      placeholder="Enter Phone number..."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['homePhoneNumber']);
                      }}
                      size={InputSize.SMALL}
                      error={questionnarieErrors?.homePhoneNumber?.message}
                      className="borderless"
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                    />
                  )}
                />
              </InfoRow>
              {additionalEmails?.map((item, index) => (
                <InfoRow
                  title={`Email #${index + 1}`}
                  id="emails"
                  key={item.id}
                  lessPadding
                >
                  <div className={cn.additionalEmailWrapper}>
                    <InputText
                      name={`email-${item.id}`}
                      placeholder="Enter email..."
                      value={item.email}
                      onChange={(e) => {
                        onChangeEmail({
                          target: {
                            name: `email-${item.id}`,
                            value: e.target.value,
                          },
                        } as ChangeEvent<HTMLInputElement>);
                        questionnarieClearErrors(['emails']);
                      }}
                      size={InputSize.SMALL}
                      className="borderless"
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                    />
                    {index > 0 &&
                      status !== NewApplicationStatus.ACCEPTED &&
                      status !== NewApplicationStatus.DECLINED && (
                        <button
                          type="button"
                          className={cn.removeEmailButton}
                          onClick={() => onDeleteAdditionalEmails(item.id)}
                        >
                          <IconClose width={16} height={16} />
                        </button>
                      )}
                  </div>
                </InfoRow>
              ))}
              <div className={cn.addSourceButtonWrapper}>
                <ButtonSecondary
                  text="Add email"
                  icon={<IconExpand width={16} height={16} />}
                  size={ButtonSize.SMALL}
                  className={`button-white ${cn.addSourceButton}`}
                  onClick={() => {
                    const nextEmails = (
                      [] as {
                        id: string;
                        email: string;
                      }[]
                    ).concat(
                      additionalEmails as {
                        id: string;
                        email: string;
                      }[],
                    );

                    nextEmails.push({
                      id: `email-${uuid()}`,
                      email: '',
                    });

                    setAdditionalEmails(nextEmails);
                  }}
                />
              </div>
              {questionnarieErrors?.emails?.message && (
                <div className={cn.stepOneQuestionnaireError}>
                  {questionnarieErrors?.emails?.message}
                </div>
              )}
              <Controller
                control={questionnarieControl}
                name="emails"
                render={({ field: { name } }) => (
                  <input type="hidden" name={name} />
                )}
              />
            </>
          )}
        </>
      </StepPartBlock>
    )}
  </>
);

export default Contacts;
